import React from "react";

/**
 * JerichoEnvironmentLayer — the cinematic black-site background.
 * Pure SVG/CSS. Renders behind everything as a fixed full-viewport scene:
 *  - distant mountain ridges
 *  - topographic contour lines
 *  - remote compound silhouette + antenna spikes
 *  - perimeter fence wireframe (vanishing into distance)
 *  - rotating radar sweep
 *  - large faint blueprint schematic
 *  - coordinate axes + targeting reticles
 */
export const JerichoEnvironmentLayer = () => {
  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none overflow-hidden"
      data-testid="jericho-environment-layer"
    >
      {/* Layer 1: sky / horizon gradient (very dark) */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, #0a0907 0%, #0a0a0c 38%, #07060a 62%, #04030a 100%), radial-gradient(ellipse at 18% 6%, rgba(217,45,32,0.18) 0%, transparent 50%), radial-gradient(ellipse at 88% 96%, rgba(93,212,179,0.08) 0%, transparent 55%)",
            backgroundBlendMode: "screen, normal, normal",
        }}
      />

      {/* Layer 2: faint blueprint schematic (huge, off-center) */}
      <svg
        className="absolute"
        style={{ top: "5%", right: "-8%", width: "70%", height: "80%", opacity: 0.07 }}
        viewBox="0 0 800 600"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="#5DD4B3" strokeWidth="0.6" fill="none">
          {/* Concentric perimeter rings */}
          <circle cx="400" cy="300" r="280" strokeDasharray="6 4" />
          <circle cx="400" cy="300" r="220" strokeDasharray="4 6" />
          <circle cx="400" cy="300" r="160" strokeOpacity="0.6" />
          <circle cx="400" cy="300" r="100" />
          {/* Cross-axes */}
          <line x1="60" y1="300" x2="740" y2="300" strokeDasharray="2 5" />
          <line x1="400" y1="0" x2="400" y2="600" strokeDasharray="2 5" />
          {/* Compound building outlines */}
          <rect x="340" y="260" width="120" height="80" />
          <rect x="280" y="230" width="40" height="30" />
          <rect x="480" y="280" width="60" height="50" />
          <rect x="370" y="360" width="60" height="40" />
          {/* Bearing markers */}
          <text x="395" y="20" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="10">N</text>
          <text x="755" y="304" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="10">E</text>
          <text x="395" y="595" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="10">S</text>
          <text x="42" y="304" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="10">W</text>
          {/* Bearing numbers */}
          <text x="500" y="120" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="7">045°</text>
          <text x="610" y="430" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="7">135°</text>
          <text x="170" y="430" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="7">225°</text>
          <text x="170" y="170" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="7">315°</text>
        </g>
        {/* Slow rotating sweep */}
        <g style={{ transformOrigin: "400px 300px", animation: "jerichoRadar 8s linear infinite" }}>
          <path d="M 400 300 L 400 20 A 280 280 0 0 1 580 80 Z" fill="rgba(93,212,179,0.10)" />
        </g>
      </svg>

      {/* Layer 3: topographic contour lines (background terrain) */}
      <svg
        className="absolute"
        style={{ bottom: "0", left: "-5%", width: "70%", height: "60%", opacity: 0.09 }}
        viewBox="0 0 600 400"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <g stroke="#CBB892" strokeWidth="0.6" fill="none">
          <path d="M -20 340 Q 120 280 260 320 T 540 290 L 640 320" />
          <path d="M -20 310 Q 140 250 300 290 T 560 260 L 640 290" />
          <path d="M -20 280 Q 160 220 320 260 T 540 235 L 640 260" />
          <path d="M -20 250 Q 180 195 320 230 T 540 210 L 640 230" />
          <path d="M -20 220 Q 200 175 340 200 T 540 185 L 640 200" />
          <path d="M -20 190 Q 220 150 360 175 T 540 165 L 640 175" />
          <path d="M -20 160 Q 240 130 380 150 T 540 145 L 640 150" />
          <path d="M -20 130 Q 260 110 400 125 T 540 125 L 640 125" />
          {/* Elevation label */}
          <text x="60" y="335" fill="#CBB892" fontFamily="ui-monospace, monospace" fontSize="6" fillOpacity="0.7">+1240m</text>
          <text x="240" y="285" fill="#CBB892" fontFamily="ui-monospace, monospace" fontSize="6" fillOpacity="0.7">+1380m</text>
          <text x="380" y="225" fill="#CBB892" fontFamily="ui-monospace, monospace" fontSize="6" fillOpacity="0.7">+1520m</text>
        </g>
      </svg>

      {/* Layer 4: mountain silhouettes (multi-depth) */}
      <svg
        className="absolute bottom-0 left-0 right-0 w-full"
        style={{ height: "42%" }}
        viewBox="0 0 1600 400"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        {/* Far ridge */}
        <polygon
          points="0,260 130,180 260,220 380,150 510,200 640,140 780,190 920,160 1060,210 1200,170 1340,220 1470,180 1600,230 1600,400 0,400"
          fill="#0d0c10"
          opacity="0.85"
        />
        {/* Mid ridge */}
        <polygon
          points="0,310 110,260 240,290 360,230 490,280 620,240 760,290 900,260 1040,300 1180,260 1320,310 1460,270 1600,320 1600,400 0,400"
          fill="#08080c"
          opacity="0.9"
        />
        {/* Near ridge with compound silhouette */}
        <polygon
          points="0,360 80,330 200,355 340,320 460,355 580,340 720,355 860,335 1000,355 1140,340 1280,355 1420,335 1600,360 1600,400 0,400"
          fill="#040409"
        />
        {/* Compound silhouette + antennae perched on near ridge */}
        <g fill="#040409" stroke="#1a1916" strokeWidth="0.5">
          <rect x="690" y="330" width="80" height="25" />
          <rect x="700" y="318" width="20" height="12" />
          <rect x="740" y="322" width="22" height="8" />
        </g>
        <g stroke="#2b2620" strokeWidth="0.8">
          <line x1="705" y1="318" x2="705" y2="296" />
          <line x1="703" y1="296" x2="708" y2="296" />
          <line x1="745" y1="322" x2="745" y2="304" />
          <line x1="755" y1="330" x2="755" y2="298" />
          <line x1="752" y1="298" x2="758" y2="298" />
        </g>
        {/* Antenna blink (red) */}
        <circle cx="705" cy="295" r="1.2" fill="#D92D20">
          <animate attributeName="opacity" values="1;0.1;1" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="755" cy="297" r="1.2" fill="#D92D20">
          <animate attributeName="opacity" values="0.1;1;0.1" dur="2.4s" repeatCount="indefinite" />
        </circle>
      </svg>

      {/* Layer 5: perimeter fence wireframe (front, vanishing point) */}
      <svg
        className="absolute bottom-0 left-0 right-0 w-full"
        style={{ height: "32%" }}
        viewBox="0 0 1600 320"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <g stroke="#D92D20" strokeOpacity="0.18" strokeWidth="0.8">
          {/* Vanishing perimeter lines */}
          <line x1="0" y1="310" x2="800" y2="180" />
          <line x1="1600" y1="310" x2="800" y2="180" />
          {/* Fence posts converging */}
          {Array.from({ length: 14 }).map((_, i) => {
            const t = i / 13;
            const xL = 0 + (800 - 0) * t;
            const xR = 1600 - (1600 - 800) * t;
            const yT = 310 - (310 - 180) * t;
            const yB = 320;
            return (
              <g key={i}>
                <line x1={xL} y1={yT} x2={xL} y2={yB} />
                <line x1={xR} y1={yT} x2={xR} y2={yB} />
              </g>
            );
          })}
          {/* Horizontal fence wires */}
          <line x1="0" y1="318" x2="1600" y2="318" />
          <line x1="0" y1="314" x2="1600" y2="314" strokeOpacity="0.12" />
          <line x1="0" y1="320" x2="1600" y2="320" />
        </g>
      </svg>

      {/* Layer 6: distant radar sweep (bottom-right) */}
      <svg
        className="absolute"
        style={{ bottom: "8%", right: "5%", width: "180px", height: "180px", opacity: 0.22 }}
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="#5DD4B3" strokeWidth="0.8" fill="none">
          <circle cx="100" cy="100" r="95" />
          <circle cx="100" cy="100" r="70" strokeDasharray="2 3" />
          <circle cx="100" cy="100" r="45" strokeDasharray="2 3" />
          <circle cx="100" cy="100" r="20" />
          <line x1="5" y1="100" x2="195" y2="100" strokeDasharray="2 4" />
          <line x1="100" y1="5" x2="100" y2="195" strokeDasharray="2 4" />
        </g>
        <g style={{ transformOrigin: "100px 100px", animation: "jerichoRadar 4s linear infinite" }}>
          <path d="M 100 100 L 100 5 A 95 95 0 0 1 195 100 Z" fill="rgba(93,212,179,0.25)" />
        </g>
        <text x="6" y="14" fill="#5DD4B3" fontFamily="ui-monospace, monospace" fontSize="7" opacity="0.8">RDR-7</text>
      </svg>

      {/* Layer 7: coordinate axes/crosshairs at edges */}
      <svg
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <g stroke="#D92D20" strokeWidth="1" strokeOpacity="0.35">
          {/* Top-left crosshair */}
          <line x1="20" y1="80" x2="50" y2="80" />
          <line x1="35" y1="65" x2="35" y2="95" />
          <circle cx="35" cy="80" r="4" fill="none" />
          {/* Bottom-right crosshair */}
          <line x1="calc(100% - 50px)" y1="calc(100% - 50px)" x2="calc(100% - 20px)" y2="calc(100% - 50px)" />
          <line x1="calc(100% - 35px)" y1="calc(100% - 65px)" x2="calc(100% - 35px)" y2="calc(100% - 35px)" />
        </g>
      </svg>

      {/* Layer 8: grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Layer 9: grain */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.85) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      {/* Layer 10: CRT scanlines */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, rgba(255,255,255,0.55) 0px, rgba(255,255,255,0.55) 1px, transparent 1px, transparent 3px)",
        }}
      />

      {/* Layer 11: vertical sweep beam */}
      <div
        className="absolute inset-x-0 h-[28%] opacity-[0.05] jericho-sweep"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(93,212,179,0.7) 50%, transparent 100%)",
        }}
      />

      {/* Layer 12: heavy vignette */}
      <div
        className="absolute inset-0"
        style={{
          boxShadow:
            "inset 0 0 300px 80px rgba(0,0,0,0.92), inset 0 0 600px 200px rgba(0,0,0,0.55)",
        }}
      />

      {/* Layer 13: top atmospheric red haze */}
      <div
        className="absolute inset-x-0 top-0 h-[28%]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(217,45,32,0.10) 0%, transparent 100%)",
        }}
      />
    </div>
  );
};

export default JerichoEnvironmentLayer;
